import React from 'react';
import Drawer from '../ui/Drawer';

interface KeyboardShortcutsModalProps {
  open: boolean;
  onClose: () => void;
}

interface ShortcutRow {
  keys: string[];
  description: string;
}

interface ShortcutSection {
  title: string;
  rows: ShortcutRow[];
}

/** Returns true when running on macOS. */
function isMac(): boolean {
  return typeof navigator !== 'undefined' && navigator.platform.includes('Mac');
}

function buildSections(): ShortcutSection[] {
  const mod = isMac() ? '⌘' : 'Ctrl';

  return [
    {
      title: 'Global',
      rows: [
        { keys: [`${mod}+K`],     description: 'Open command palette'         },
        { keys: ['Escape'],        description: 'Close any modal / panel'      },
        { keys: ['?'],             description: 'Open keyboard shortcuts'      },
      ],
    },
    {
      title: 'Navigation',
      rows: [
        { keys: ['G', 'D'],        description: 'Go to Dashboard'              },
        { keys: ['G', 'P'],        description: 'Go to Performance'            },
        { keys: ['G', 'H'],        description: 'Go to HR'                     },
        { keys: ['G', 'T'],        description: 'Go to Tasks'                  },
        { keys: ['G', 'S'],        description: 'Go to Support'                },
        { keys: ['G', 'C'],        description: 'Go to CRM'                    },
      ],
    },
    {
      title: 'Pages',
      rows: [
        { keys: ['/'],             description: 'Focus search bar'             },
        { keys: ['N'],             description: 'New item'                     },
        { keys: ['E'],             description: 'Edit selected item'           },
        { keys: ['Delete', 'D'],   description: 'Delete selected item'        },
      ],
    },
    {
      title: 'Tables',
      rows: [
        { keys: ['↑', '↓'],        description: 'Navigate rows'               },
        { keys: ['Enter'],         description: 'Open row detail'              },
        { keys: ['Escape'],        description: 'Close detail panel'           },
      ],
    },
  ];
}

interface KeyBadgeProps {
  label: string;
}

function KeyBadge({ label }: KeyBadgeProps) {
  return (
    <kbd className="inline-flex items-center justify-center bg-white/15 border border-white/20 rounded px-2 py-0.5 text-xs font-mono text-white leading-none">
      {label}
    </kbd>
  );
}

interface ShortcutRowItemProps {
  row: ShortcutRow;
}

function ShortcutRowItem({ row }: ShortcutRowItemProps) {
  return (
    <div className="flex items-center gap-3 py-1.5">
      <div className="flex items-center gap-1 shrink-0 min-w-[6rem]">
        {row.keys.map((key, i) => (
          <React.Fragment key={`${key}-${i}`}>
            {i > 0 && (
              <span className="text-white/30 text-xs select-none">then</span>
            )}
            <KeyBadge label={key} />
          </React.Fragment>
        ))}
      </div>
      <span className="text-sm text-white/70">{row.description}</span>
    </div>
  );
}

interface SectionCardProps {
  section: ShortcutSection;
}

function SectionCard({ section }: SectionCardProps) {
  return (
    <div
      className="rounded-xl border border-white/10 p-4"
      style={{ background: 'rgba(255,255,255,0.06)' }}
    >
      <h3 className="text-xs font-semibold uppercase tracking-widest text-white/40 mb-3">
        {section.title}
      </h3>
      <div className="space-y-0.5">
        {section.rows.map(row => (
          <ShortcutRowItem key={row.description} row={row} />
        ))}
      </div>
    </div>
  );
}

export default function KeyboardShortcutsModal({ open, onClose }: KeyboardShortcutsModalProps) {
  const sections = buildSections();

  // Split sections into two columns (left = first two, right = last two)
  const leftSections  = sections.slice(0, 2);
  const rightSections = sections.slice(2);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Keyboard Shortcuts"
      subtitle={<>Press <span className="font-mono">?</span> anywhere to toggle this panel</>}
      width={600}
    >
      {/* Two-column grid (single column on mobile / full-width drawer) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-4">
          {leftSections.map(section => (
            <SectionCard key={section.title} section={section} />
          ))}
        </div>
        <div className="space-y-4">
          {rightSections.map(section => (
            <SectionCard key={section.title} section={section} />
          ))}
        </div>
      </div>

      <p className="text-xs text-white/25 text-center mt-5">
        Navigation shortcuts work when focus is outside an input field
      </p>
    </Drawer>
  );
}
